<script setup>
import {onMounted, ref, defineProps} from 'vue'
import moment from 'moment'

import User from '@/api/User'
import { formatSourcesLabel } from '@/components/EventSearch/sourcesLabelUtils'

import { useMainStore } from '@/stores/main'
const mainStore = useMainStore()

import { useRouter } from 'vue-router'
const router = useRouter()

import AllQueriesView from '@/components/SlideOver/AllQueriesView.vue'

const props = defineProps({
  filterType: {
    type: String,
    default: null,
    validator: (value) => [null, 'ip', 'domain', 'event', 'ja4'].includes(value)
  }
})

const queries = ref([])

onMounted(async () => {
  await fetchQueries()
})

async function fetchQueries() {
  try {
    const response = await User.getQueries(0, 50, '', '', '', props.filterType || '')
    queries.value = response.data.queries || []
  } catch (e) {
    console.error('Failed to fetch queries', e)
    queries.value = []
  }
}

async function exec_query(input, query_type, sources) {
  // Route event queries to EventSearch view
  if (['event', 'event_search'].includes(query_type)) {
    const routeQuery = {
      query: input.trim(),
      log: 'false' // Don't re-log historical queries
    }
    if (sources) routeQuery.sources = sources
    router.push({
      name: 'EventSearch',
      query: routeQuery
    })
  } else if (query_type === 'selector') {
    // Combined selector search: no tab — the results page activates the
    // leftmost present type.
    router.push({ name: 'SelectorResults', query: { input: input.trim(), log: 'false' } })
  } else if (['ip', 'domain', 'ja4'].includes(query_type)) {
    // Legacy single-result types → results page zoomed on the value. Saved
    // ja4 queries were stored as "<fingerprint>,<ja4 type>" — keep only the
    // fingerprint (the carver detects the subtype).
    const value = input.trim().split(',')[0].trim()
    router.push({ name: 'SelectorResults', query: { input: value, tab: query_type, zoom: value, log: 'false' } })
  } else if (['ip_table', 'domain_table', 'ja4_table'].includes(query_type)) {
    // Legacy bulk types → results page on the matching tab, no zoom.
    router.push({ name: 'SelectorResults', query: { input: input.trim(), tab: query_type.replace('_table', ''), log: 'false' } })
  } else {
    // Fallback for other query types
    let value = input.trim().split(",")
    router.push({
      name: 'SearchResults',
      query: {
        input: Array.isArray(value) ? value.join(',') : value,
        type: query_type,
        log: 'false'
      }
    })
  }
}

function showAllQueries() {
  mainStore.show_slideover(AllQueriesView, { filterType: props.filterType }, { width: 50 }, "Search History")
}

function pretty_print(query_type) {
  if (query_type == "ip") return "Ip"
  else if (query_type == "ip_table") return "Ip Bulk"
  else if (query_type == "subdomains") return "Subdomains"
  else if (query_type == "emails") return "Emails"
  else if (query_type == "similar") return "Similar domains"
  else if (query_type == "reverse_dns") return "Reverse DNS"
  else if (query_type == "fingerprint_ips") return "Fingerprint to Ips"
  else if (query_type == "ip_fingerprints") return "Ip fingerprints"
  else if (query_type == "signals_toptalkers") return "Signals Toptalkers"
  else if (query_type == "domain") return "Domain"
  else if (query_type == "domain_table") return "Domain Bulk"
  else if (query_type == "ja4") return "JA4+"
  else if (query_type == "ja4_table") return "JA4+ Bulk"
  else if (query_type == "selector") return "Selectors"
  else if (query_type == "event" || query_type == "event_search") return "Netflow Search"
  else return query_type
}

</script>


<template>
  <div class="b">
    <div v-if="queries.length > 0" class="history-content">
      <div class="history-header">
        <div class="history-label">Recent Searches</div>
        <button @click="showAllQueries" class="view-all-button">
          View All
          <font-awesome-icon icon="arrow-right" class="ms-1" />
        </button>
      </div>
      <div class="history-grid">
        <div
          v-for="query in queries"
          :key="query.id"
          class="history-chip"
          @click="exec_query(query.query, query.type, query.sources)"
        >
          <div class="history-chip-left">
            <span class="history-type" :class="{ 'event-type': query.type === 'event' || query.type === 'event_search', 'ja4-type': query.type === 'ja4' || query.type === 'ja4_table' }">{{ pretty_print(query.type) }}</span>
            <span class="history-time">{{ moment(query.timestamp).fromNow() }}</span>
          </div>
          <div class="history-query-content">
            <code class="history-query">{{ query.query }}</code>
            <span v-if="['event', 'event_search'].includes(query.type) && formatSourcesLabel(query.sources)" class="sources-badge">{{ formatSourcesLabel(query.sources) }}</span>
          </div>
          <div class="history-credits">{{ query.credits_used }} credits</div>
        </div>
      </div>
    </div>
    <div v-else class="empty-state">
      <font-awesome-icon icon="magnifying-glass" class="empty-icon" />
      <p class="empty-title">No search history</p>
      <p class="empty-description">Your search history will appear here</p>
    </div>
  </div>
</template>

<style scoped>
.b {
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.history-content {
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.history-header {
  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.history-label {
  color: var(--text-muted);
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.view-all-button {
  background-color: transparent;
  border: 1px solid var(--border-subtle);
  color: var(--text-faint);
  padding: 0.5rem 1rem;
  border-radius: var(--radius-md);
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.view-all-button:hover {
  background-color: var(--surface-overlay-2);
  color: var(--text-primary);
}

.history-grid {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 0.5rem;
}

.history-grid::-webkit-scrollbar {
  width: 6px;
}

.history-grid::-webkit-scrollbar-track {
  background: var(--surface-overlay-2);
  border-radius: var(--radius-xs);
}

.history-grid::-webkit-scrollbar-thumb {
  background: rgba(168, 85, 247, 0.3);
  border-radius: var(--radius-xs);
}

.history-grid::-webkit-scrollbar-thumb:hover {
  background: rgba(168, 85, 247, 0.5);
}

.history-chip {
  background: rgba(26, 29, 36, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: var(--radius-sm);
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: var(--transition);
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 1rem;
}

.history-chip:hover {
  background: rgba(168, 85, 247, 0.08);
  border-color: rgba(168, 85, 247, 0.3);
}

.history-chip-left {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 150px;
}

.history-type {
  color: var(--accent-text);
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.history-type.event-type {
  color: var(--color-info);
}

.history-type.ja4-type {
  color: var(--color-success);
}

.history-time {
  color: var(--text-faint);
  font-size: 0.7rem;
}

.history-query-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
  overflow: hidden;
}

.history-query {
  color: var(--accent-text);
  font-size: 0.875rem;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.sources-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.125rem 0.5rem;
  background: rgba(52, 211, 153, 0.15);
  border-radius: var(--radius-sm);
  font-size: 0.675rem;
  color: var(--color-success-text);
  white-space: nowrap;
  flex-shrink: 0;
}

.history-credits {
  color: var(--text-faint);
  font-size: 0.75rem;
  text-align: right;
  white-space: nowrap;
}

.history-chip:hover .history-time,
.history-chip:hover .history-credits {
  color: var(--text-muted);
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  color: var(--text-faint);
}

.empty-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.empty-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-muted);
  margin-bottom: 0.5rem;
}

.empty-description {
  font-size: 0.875rem;
  color: var(--text-faint);
  margin: 0;
}

</style>
